import { UseQueryResult, useQuery } from '@tanstack/react-query'
import Cookies from 'js-cookie'

import {
  ConditionsReportDocumentResponse,
  ConditionsReportResponse,
} from '@nx/api'
import { createAuthenticatedAxiosClient } from '@nx/axios-client'
import { populateRelativeUrl } from '@nx/populate-url'

export function useGetConditionsReportDocument(
  lotId: number,
  baseURL: string,
  conditionsReportParams?: ConditionsReportResponse
): UseGetConditionsReportDocumentReturn {
  const cookies = Cookies.get()

  const authenticatedAxiosClient = createAuthenticatedAxiosClient({
    accessTokenSSR: cookies?.bonhams_access || '',
    refreshTokenSSR: cookies?.bonhams_token || '',
    baseURL,
  })

  let url = `/api/auctions/condition-report/${lotId}/redirect/`

  if (conditionsReportParams?.redirect) {
    const { redirect } = conditionsReportParams
    url = populateRelativeUrl(url, redirect)
  }

  const { data, status, fetchStatus, error, refetch } = useQuery({
    queryKey: ['conditionsReport', url],
    queryFn: async () => {
      try {
        const response = await authenticatedAxiosClient.get(url, {
          responseType: 'arraybuffer',
          headers: { Accept: 'application/pdf, text/plain, */*' },
        })

        const attachment = response.headers['content-disposition']
        const contentType = response.headers['content-type']

        if (attachment) {
          const filename = attachment.split('filename=')[1].replace(/"/g, '')
          const documentUrl = URL.createObjectURL(
            new Blob([response.data], {
              type: contentType,
            })
          )

          return { documentUrl, filename }
        } else {
          throw new Error('Failed to get conditions report')
        }
      } catch {
        throw new Error('Failed to get conditions report')
      }
    },
    retry: 3,
    retryDelay: 1_000,
    enabled: Boolean(conditionsReportParams?.redirect.includes('saction=Show')),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
    retryOnMount: false,
  })

  return {
    data,
    status,
    fetchStatus,
    error,
    refetch,
  }
}

export interface UseGetConditionsReportDocumentReturn
  extends Pick<UseQueryResult, 'status' | 'fetchStatus' | 'refetch'> {
  data?: ConditionsReportDocumentResponse
  error: Error | null
}
